import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaWeixin, FaCommentDots } from 'react-icons/fa';

/**
 * 公式アカウント QR コードフロート（マイページ専用 - 強化版 CTA）
 */
const WeChatGroupFloat: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    const lang = i18n.language.toLowerCase();
    const isZh = lang.startsWith('zh');
    
    // コンテンツの切り替え
    const title = isZh ? '关注官方公众号' : '公式 LINE アカウント';
    const ctaText = isZh ? '联系我们' : '公式アカウント';
    const subtitle = isZh ? '获取最新资讯与预约服务' : '最新情報と予約はこちら';
    const qrSrc = isZh ? '/images/wechat_mp_qr.jpg' : '/images/line_qr.png';
    const qrAlt = isZh ? 'WeChat QR Code' : 'LINE QR Code';
    const scanLabel = isZh 
        ? '请用手机微信扫一扫关注我们' 
        : 'LINEでかざして友だち追加してください';
    const savedLabel = isZh
        ? '手机用户请长按保存，并在微信内使用扫一扫功能'
        : 'スマートフォンの方は長押しで保存して読み込んでください';

    return (
        <>
            <style>
                {`
                @keyframes pulse-subtle {
                    0% { transform: scale(1); box-shadow: 0 10px 15px -3px rgba(249, 115, 22, 0.3), 0 4px 6px -2px rgba(249, 115, 22, 0.05); }
                    50% { transform: scale(1.03); box-shadow: 0 20px 25px -5px rgba(249, 115, 22, 0.4), 0 10px 10px -5px rgba(249, 115, 22, 0.1); }
                    100% { transform: scale(1); box-shadow: 0 10px 15px -3px rgba(249, 115, 22, 0.3), 0 4px 6px -2px rgba(249, 115, 22, 0.05); }
                }
                .animate-pulse-subtle {
                    animation: pulse-subtle 3s infinite ease-in-out;
                }
                `}
            </style>

            {/* 左下のコンテキストに応じた Pill 形状のボタン */}
            <button
                onClick={() => setIsExpanded(true)}
                style={{ position: 'fixed', bottom: '1.5rem', left: '1.5rem' }}
                className="z-50 px-5 py-3 bg-coral hover:bg-coral-dark text-white rounded-full shadow-2xl transition-all duration-300 flex items-center gap-3 group active:scale-95 animate-pulse-subtle"
                aria-label={title}
            >
                <div className="relative">
                    {isZh ? (
                        <FaWeixin className="w-6 h-6" />
                    ) : (
                        <FaCommentDots className="w-5 h-5" />
                    )}
                    {/* オンラインインジケーター */}
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 border-2 border-coral rounded-full"></span>
                </div>
                <span className="font-bold text-sm tracking-wide whitespace-nowrap">
                    {ctaText}
                </span>
            </button>

            {/* 展開時のモーダル */}
            {isExpanded && (
                <div
                    className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => setIsExpanded(false)}
                >
                    <div
                        className="bg-white rounded-[2.5rem] shadow-2xl p-8 max-w-[340px] w-full border border-gray-100 animate-scale-in relative overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* 装飾用の背景 */}
                        <div className="absolute top-0 left-0 w-full h-2 bg-coral"></div>

                        {/* ヘッダー */}
                        <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-coral/10 rounded-2xl flex items-center justify-center text-coral">
                                    {isZh ? <FaWeixin className="w-7 h-7" /> : <FaCommentDots className="w-6 h-6" />}
                                </div>
                                <div className="flex flex-col">
                                    <h3 className="font-extrabold text-lg text-gray-800 leading-tight whitespace-nowrap">{title}</h3>
                                    <span className="text-xs text-slate-500 font-medium">{subtitle}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsExpanded(false)}
                                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 transition-all hover:rotate-90 duration-300"
                            >
                                <FaTimes className="w-5 h-5" />
                            </button>
                        </div>

                        {/* QR コード */}
                        <div className="bg-slate-50 rounded-[2rem] p-6 mb-2 flex flex-col items-center border border-slate-100 shadow-inner">
                            <div className="bg-white p-3 rounded-2xl shadow-sm mb-5 transition-transform hover:scale-105 duration-300">
                                <img
                                    src={qrSrc}
                                    alt={qrAlt}
                                    className="w-48 h-48 object-contain"
                                />
                            </div>
                            <div className="text-center space-y-2">
                                <p className="text-[13px] font-bold text-gray-800 whitespace-nowrap">
                                    {scanLabel}
                                </p>
                                <p className="text-[10px] text-slate-400 leading-relaxed max-w-[200px]">
                                    {savedLabel}
                                </p>
                            </div>
                        </div>

                        {/* 下部のヒント */}
                        <p className="text-[10px] text-slate-300 text-center mt-6 font-medium">
                            {isZh ? '点击弹窗外部即可关闭' : '枠線の外をクリックして閉じます'}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

export default WeChatGroupFloat;
